import { ProNavigation } from "@/components/ProNavigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";

const remediesUnitaires = [
  {
    id: "aubepine",
    name: "Aubépine",
    latinName: "Crataegus oxyacantha",
    emoji: "🌳",
    effects: "Régulateur cardiaque, apaisant nerveux, hypotenseur.",
    indications: ["Hypertension", "Tachycardie", "Anxiété", "Troubles du sommeil", "Palpitations"]
  },
  {
    id: "bouleau-verruqueux",
    name: "Bouleau verruqueux",
    latinName: "Betula verrucosa",
    emoji: "🌳",
    effects: "Drainage général, anti-inflammatoire, reminéralisant.",
    indications: ["Rhumatisme", "Allergies", "Drainage printanier", "Acidose", "Détox foie-reins"]
  },
  {
    id: "bouleau-pubescent",
    name: "Bouleau pubescent",
    latinName: "Betula pubescens",
    emoji: "🌳",
    effects: "Stimulant rénal, dépuratif, tonique immunitaire.",
    indications: ["Fatigue", "Infections ORL chroniques", "Drainage rénal"]
  },
  {
    id: "cassis",
    name: "Cassis",
    latinName: "Ribes nigrum",
    emoji: "🌳",
    effects: "Anti-inflammatoire majeur, cortison-like naturel, antihistaminique.",
    indications: ["Allergies", "Inflammations", "Arthrite", "Asthme", "Immunité faible"]
  },
  {
    id: "frene",
    name: "Frêne",
    latinName: "Fraxinus excelsior",
    emoji: "🌳",
    effects: "Antirhumatismal, drainant acide urique, diurétique.",
    indications: ["Arthrite", "Goutte", "Œdèmes", "Surcharges articulaires"]
  },
  {
    id: "figuier",
    name: "Figuier",
    latinName: "Ficus carica",
    emoji: "🌳",
    effects: "Régulateur nerveux, antispasmodique digestif, anti-ulcéreux.",
    indications: ["Stress", "Gastrite", "Reflux", "Anxiété digestive", "Troubles du sommeil"]
  },
  {
    id: "genevrier",
    name: "Genévrier",
    latinName: "Juniperus communis",
    emoji: "🌳",
    effects: "Détox hépatique & rénale, antiseptique, stimulant digestif.",
    indications: ["Insuffisance hépatique", "Dyspepsie", "Digestion lente", "Cure dépurative"]
  },
  {
    id: "eglantier",
    name: "Églantier",
    latinName: "Rosa canina",
    emoji: "🌳",
    effects: "Stimulant immunitaire enfant, anti-inflammatoire ORL.",
    indications: ["Otites", "Rhinites", "Angines", "ORL chroniques enfant/adulte"]
  },
  {
    id: "noisetier",
    name: "Noisetier",
    latinName: "Corylus avellana",
    emoji: "🌳",
    effects: "Anti-fibrosant, stimulant circulatoire, soutien pulmonaire.",
    indications: ["Fibrose", "Insuffisance respiratoire", "Troubles circulatoires"]
  },
  {
    id: "noyer",
    name: "Noyer",
    latinName: "Juglans regia",
    emoji: "🌳",
    effects: "Antiseptique intestinal, régénération flore, purifiant.",
    indications: ["Dysbiose", "Diarrhée chronique", "Maladies de peau", "Mycoses"]
  },
  {
    id: "tilleul",
    name: "Tilleul",
    latinName: "Tilia tomentosa",
    emoji: "🌳",
    effects: "Sédatif, régulateur nerveux, antispasmodique.",
    indications: ["Stress", "Insomnie", "Anxiété", "Agitation", "Spasmes digestifs"]
  },
  {
    id: "romarin",
    name: "Romarin",
    latinName: "Rosmarinus officinalis",
    emoji: "🌳",
    effects: "Hépatoprotecteur, détoxifiant, stimulant cérébral.",
    indications: ["Fatigue hépatique", "Cholestérol", "Manque de concentration"]
  },
  {
    id: "vigne",
    name: "Vigne",
    latinName: "Vitis vinifera",
    emoji: "🌳",
    effects: "Anti-inflammatoire articulaire, anti-oxydant, anti-hyperplasique.",
    indications: ["Arthrose", "Polyarthrite", "Maladies inflammatoires chroniques"]
  },
  {
    id: "marronnier",
    name: "Marronnier",
    latinName: "Aesculus hippocastanum",
    emoji: "🌳",
    effects: "Tonique veineux, anti-œdème, décongestionnant.",
    indications: ["Varices", "Hémorroïdes", "Jambes lourdes"]
  },
  {
    id: "sequoia",
    name: "Séquoia",
    latinName: "Sequoia gigantea",
    emoji: "🌳",
    effects: "Tonique général masculin, reminéralisant, stimulant sexuel.",
    indications: ["Fatigue", "Perte de libido", "Ostéoporose", "Vieillissement masculin"]
  },
  {
    id: "chene",
    name: "Chêne",
    latinName: "Quercus robur",
    emoji: "🌳",
    effects: "Tonique général, stimulant endocrinien, contre la fatigue profonde.",
    indications: ["Burn-out", "Asthénie", "Hypotension"]
  },
  {
    id: "peuplier",
    name: "Peuplier",
    latinName: "Populus nigra",
    emoji: "🌳",
    effects: "Anti-inflammatoire vasculaire, fluidifiant sanguin.",
    indications: ["Artérite", "Troubles circulatoires", "Varices douloureuses"]
  },
  {
    id: "aulne-glutineux",
    name: "Aulne glutineux",
    latinName: "Alnus glutinosa",
    emoji: "🌳",
    effects: "Anti-inflammatoire vasculaire, stimulant cérébral.",
    indications: ["Migraines", "Sinusites chroniques", "Phlébite (prévention)"]
  },
  {
    id: "saule",
    name: "Saule",
    latinName: "Salix alba",
    emoji: "🌳",
    effects: "Anti-inflammatoire, antalgique, fébrifuge (salicylates naturels).",
    indications: ["Fièvre", "Douleurs", "Arthrose", "États grippaux"]
  },
  {
    id: "sorbier",
    name: "Sorbier",
    latinName: "Sorbus domestica",
    emoji: "🌳",
    effects: "Tonique veineux et lymphatique.",
    indications: ["Œdèmes", "Jambes lourdes", "Rétention d'eau"]
  },
  {
    id: "cornouiller",
    name: "Cornouiller",
    latinName: "Cornus sanguinea",
    emoji: "🌳",
    effects: "Action cardiovasculaire, améliore irrigation tissulaire.",
    indications: ["Troubles circulatoires", "Fragilité vasculaire"]
  },
  {
    id: "frene-fleurs",
    name: "Frêne à fleurs",
    latinName: "Fraxinus ornus",
    emoji: "🌳",
    effects: "Drainage urique et lipidique.",
    indications: ["Goutte", "Cholestérol", "Surpoids inflammatoire"]
  },
  {
    id: "bouleau-rouge",
    name: "Bouleau rouge",
    latinName: "Betula alba",
    emoji: "🌳",
    effects: "Détox puissant, stimulant immunitaire.",
    indications: ["Allergies", "Cure dépurative"]
  },
  {
    id: "sapin-pectine",
    name: "Sapin pectiné",
    latinName: "Abies pectinata",
    emoji: "🌳",
    effects: "Ossification, reminéralisant, croissance.",
    indications: ["Ostéoporose", "Retard de croissance", "Fractures"]
  },
  {
    id: "hetre",
    name: "Hêtre",
    latinName: "Fagus sylvatica",
    emoji: "🌳",
    effects: "Stimulant immunitaire, anti-œdème.",
    indications: ["Déficience immunitaire", "Rétention d'eau"]
  },
  {
    id: "olivier",
    name: "Olivier",
    latinName: "Olea europaea",
    emoji: "🌳",
    effects: "Hypotenseur, protecteur cardiovasculaire.",
    indications: ["Hypertension", "Cholestérol"]
  },
  {
    id: "amande-douce",
    name: "Amande douce",
    latinName: "Prunus amygdalus",
    emoji: "🌳",
    effects: "Régulateur lipidique, doux laxatif.",
    indications: ["Hyperlipidémie", "Constipation"]
  },
  {
    id: "viorne",
    name: "Viorne",
    latinName: "Viburnum lantana",
    emoji: "🌳",
    effects: "Antispasmodique respiratoire.",
    indications: ["Asthme", "Bronchiolite", "Toux spasmodiques"]
  },
  {
    id: "cassis-jeunes-pousses",
    name: "Cassis bourgeons jeunes pousses",
    latinName: "Ribes nigrum",
    emoji: "🌳",
    effects: "Cortisonique naturel majeur (action renforcée).",
    indications: ["Allergies chroniques", "Inflammations aiguës"]
  },
  {
    id: "rosier-sauvage",
    name: "Rosier sauvage (jeunes pousses)",
    latinName: "Rosa canina",
    emoji: "🌳",
    effects: "Renforce immunité des enfants.",
    indications: ["ORL récurrentes chez l'enfant"]
  },
  {
    id: "framboisier",
    name: "Framboisier",
    latinName: "Rubus idaeus",
    emoji: "🌳",
    effects: "Régulateur hormonal féminin.",
    indications: ["Syndrome prémenstruel", "Cycles irréguliers", "Ménopause"]
  },
  {
    id: "airelle",
    name: "Airelle",
    latinName: "Vaccinium vitis-idaea",
    emoji: "🌳",
    effects: "Antiseptique urinaire, progestérone-like.",
    indications: ["Cystites", "Troubles hormonaux", "Transit paresseux"]
  },
  {
    id: "myrtillier",
    name: "Myrtillier",
    latinName: "Vaccinium myrtillus",
    emoji: "🌳",
    effects: "Protection vasculaire et oculaire.",
    indications: ["Rétinopathies", "Fragilité capillaire", "Diabète"]
  },
  {
    id: "orme",
    name: "Orme",
    latinName: "Ulmus campestris",
    emoji: "🌳",
    effects: "Drainage cutané, dépuratif dermique.",
    indications: ["Eczéma", "Psoriasis", "Peau chargée"]
  },
  {
    id: "pommier",
    name: "Pommier",
    latinName: "Malus domestica",
    emoji: "🌳",
    effects: "Régulateur nerveux, adoucissant digestif.",
    indications: ["Stress", "Spasmes digestifs", "Troubles émotionnels"]
  },
  {
    id: "poirier",
    name: "Poirier",
    latinName: "Pyrus communis",
    emoji: "🌳",
    effects: "Régulateur digestif et urinaire.",
    indications: ["Colites", "Cystites", "Troubles urinaires"]
  },
  {
    id: "tilleul-jeunes",
    name: "Tilleul bourgeons jeunes",
    latinName: "Tilia tomentosa",
    emoji: "🌳",
    effects: "Sédatif profond, harmonisant nerveux.",
    indications: ["Stress intense", "Insomnie", "Hyperactivité"]
  },
  {
    id: "chevrefeuille",
    name: "Chèvrefeuille",
    latinName: "Lonicera nigra",
    emoji: "🌳",
    effects: "Anti-inflammatoire ORL.",
    indications: ["Rhinite", "Sinusite chronique"]
  },
  {
    id: "ronce",
    name: "Ronce",
    latinName: "Rubus fruticosus",
    emoji: "🌳",
    effects: "Régénérant pulmonaire et osseux.",
    indications: ["Fibrose pulmonaire", "Ostéoporose"]
  },
  {
    id: "aulne-blanc",
    name: "Aulne blanc",
    latinName: "Alnus incana",
    emoji: "🌳",
    effects: "Anti-inflammatoire articulaire.",
    indications: ["Rhumatisme", "Arthrose"]
  }
];

export default function GemmotherapyLibrary() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-hero py-8 px-4">
      <ProNavigation />
      
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:justify-between">
          <Button variant="outline" size="sm" onClick={() => navigate('/treatments-library')}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Bibliothèque de Traitements</span>
            <span className="sm:hidden">Retour</span>
          </Button>
          <h1 className="text-xl sm:text-3xl font-bold text-primary">🌱 Gemmothérapie</h1>
          <div className="hidden sm:block w-[140px]" />
        </div>

        {/* 1. Introduction */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-2xl">🌿 1. Introduction à la Gemmothérapie</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              La gemmothérapie est une méthode de soin utilisant les <strong>bourgeons</strong> et <strong>jeunes pousses</strong> des plantes. 
              Le mot "gemmothérapie" vient du latin "<em>gemma</em>" (pierre précieuse ou bourgeon), symbolisant la richesse thérapeutique des bourgeons 🌟
            </p>
          </CardContent>
        </Card>

        {/* 2. Principe */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-2xl">🌸 2. Le Principe de la Gemmothérapie</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold text-primary mb-2">🌱 Récolte des bourgeons</h4>
              <p className="text-sm text-muted-foreground">On récolte des bourgeons frais ou des jeunes pousses pendant leur croissance.</p>
            </div>
            
            <div>
              <h4 className="font-semibold text-primary mb-2">🧪 Macération des bourgeons</h4>
              <p className="text-sm text-muted-foreground mb-2">Les bourgeons sont placés dans une solution composée de 3 solvants :</p>
              <div className="grid md:grid-cols-3 gap-3">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="font-medium text-blue-700">💧 Eau</p>
                  <p className="text-xs text-blue-600">Pour les principes actifs hydrosolubles</p>
                </div>
                <div className="bg-amber-50 p-3 rounded-lg">
                  <p className="font-medium text-amber-700">🍯 Glycérine</p>
                  <p className="text-xs text-amber-600">Pour les principes actifs liposolubles</p>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg">
                  <p className="font-medium text-purple-700">🍸 Alcool</p>
                  <p className="text-xs text-purple-600">Pour homogénéiser et extraire les principes actifs</p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-green-800 font-medium">✅ Résultat</p>
              <p className="text-green-700 text-sm">On obtient un <strong>macérat glycériné</strong>, riche en bienfaits pour la santé 💪</p>
            </div>
          </CardContent>
        </Card>

        {/* 3. Composition */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-2xl">🌿 3. Composition des Bourgeons</CardTitle>
            <CardDescription>Les bourgeons sont des tissus embryonnaires contenant :</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2 mb-4">
              <Badge variant="secondary">🧬 Acides nucléiques (régénération)</Badge>
              <Badge variant="secondary">🌱 Hormones de croissance</Badge>
              <Badge variant="secondary">🍊 Vitamines</Badge>
              <Badge variant="secondary">🧂 Minéraux</Badge>
              <Badge variant="secondary">🧪 Enzymes</Badge>
              <Badge variant="secondary">💧 Sève minérale</Badge>
              <Badge variant="secondary">⚡ Oligo-éléments</Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              Ces éléments confèrent aux bourgeons une <strong>puissante action régénératrice</strong> sur le corps 🌟
            </p>
          </CardContent>
        </Card>

        {/* 4. Posologie */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-2xl">💊 4. Posologie</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-blue-700 mb-2">👤 Général</h4>
              <p className="text-blue-800 font-medium">De 5 à 15 gouttes par jour, selon la personne et la pathologie</p>
              <ul className="text-sm text-blue-700 mt-2 space-y-1">
                <li>• Commencer à 5 gouttes si sensible, augmenter progressivement</li>
                <li>• Un adulte peut débuter à 15 gouttes et ajuster selon ses ressentis</li>
              </ul>
            </div>

            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-semibold text-green-700 mb-2">👶 Enfants</h4>
              <p className="text-green-800">
                <strong>1 goutte par année d'âge</strong>
              </p>
              <p className="text-sm text-green-700 mt-1">
                Exemple : un enfant de 3 ans prendra 3 gouttes, un de 16 ans jusqu'à 15 gouttes
              </p>
            </div>

            <div className="bg-pink-50 p-4 rounded-lg">
              <h4 className="font-semibold text-pink-700 mb-2">🤰 Femmes enceintes et allaitantes</h4>
              <ul className="text-sm text-pink-700 space-y-2">
                <li><strong>Allaitement 👩‍🍼 :</strong> La mère prend le bourgeon et le bébé en profite via le lait</li>
                <li><strong>Grossesse 🤰 :</strong> Éviter les bourgeons hormonaux (framboisier, séquoia), privilégier ceux qui soutiennent l'immunité comme le cassis et le sapin 🎄</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* 5. Conservation */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-2xl">🧴 5. Conservation</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-muted-foreground">
              <li>• Les produits se conservent environ <strong>4 ans</strong></li>
              <li>• Utiliser rapidement après ouverture</li>
              <li>• Pour les complexes de bourgeons, <strong>ne pas prendre plus de 2 complexes par jour</strong> ⚠️</li>
            </ul>
          </CardContent>
        </Card>

        {/* 6. Remèdes Unitaires */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-2xl">🌿 6. Remèdes Unitaires</CardTitle>
            <CardDescription>Exemples de bourgeons utilisés individuellement</CardDescription>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="w-full">
              {remediesUnitaires.map((remedy) => (
                <AccordionItem key={remedy.id} value={remedy.id}>
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{remedy.emoji}</span>
                      <div className="text-left">
                        <span className="font-semibold">{remedy.name}</span>
                        <span className="text-muted-foreground ml-2 text-sm italic">({remedy.latinName})</span>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 pl-10">
                      <div>
                        <h4 className="font-semibold text-blue-700 mb-2">💫 Effets</h4>
                        <p className="text-muted-foreground">{remedy.effects}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-green-700 mb-2">✅ Indications</h4>
                        <div className="flex flex-wrap gap-2">
                          {remedy.indications.map((ind, i) => (
                            <Badge key={i} variant="outline">{ind}</Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>

        {/* 7. Complexes */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-2xl">🔬 7. Complexes de Bourgeons</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              Les complexes regroupent plusieurs bourgeons pour une action plus large et optimisée. 
              Ces solutions sont pratiques et efficaces pour traiter des problèmes plus complexes.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-amber-800 font-medium">⚠️ Précaution</p>
              <p className="text-amber-700 text-sm">
                Ne pas prendre plus de 2 complexes par jour. Espacer les prises (un le matin, un le soir) 🕒
              </p>
            </div>
          </CardContent>
        </Card>

        {/* 8. Précautions */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-2xl">⚠️ 8. Précautions et Contre-indications</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded-lg">
                <h4 className="font-semibold text-red-700 mb-2">🚫 Hypertension</h4>
                <p className="text-red-600 text-sm">
                  Éviter les bourgeons tonifiants comme le <strong>romarin, cassis, séquoia et chêne</strong>
                </p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg">
                <h4 className="font-semibold text-red-700 mb-2">🩸 Fluidifiants sanguins</h4>
                <p className="text-red-600 text-sm">
                  Éviter les bourgeons comme le <strong>citronnier et le ginkgo</strong> qui fluidifient également le sang
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 9. Conclusion */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-2xl">💡 9. Conclusion</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-muted-foreground">
                La gemmothérapie offre un excellent moyen de profiter des bienfaits complets des bourgeons, 
                avec des remèdes naturels et pratiques (<strong>5-15 gouttes par jour</strong>).
              </p>
              <p className="text-muted-foreground">
                Grâce à la macération dans eau, alcool et glycérine, on extrait une large gamme de principes actifs 
                pour des traitements efficaces 🌟
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <p className="text-green-800 font-medium text-lg">
                  🌱 Les bourgeons, grâce à leur richesse énergétique, jouent un rôle clé dans la régénération cellulaire 
                  et le soutien de l'immunité et des organes principaux du corps 💪
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Résumé visuel */}
        <Card>
          <CardContent className="py-6">
            <div className="text-center">
              <p className="text-4xl mb-4">🌱</p>
              <p className="text-lg font-medium text-primary">
                La gemmothérapie : la médecine des bourgeons
              </p>
              <p className="text-muted-foreground mt-2">
                Un indispensable de la trousse naturopathique pour la régénération et le soutien immunitaire ! 🎒
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
